import { useEffect, useRef, useState } from "react";
import type { SupportInfo } from "@hik/contracts";
import { useT } from "../lib/i18n";
import { useSession } from "../lib/session";
import { endSupportAndReturn } from "../lib/support";
import { Icon } from "../ui/Icon";

function minutesLeft(expiresAt: string) {
  return Math.max(0, Math.ceil((Date.parse(expiresAt) - Date.now()) / 60_000));
}

/**
 * Destek şeridi: 21ik ekibi firma gözünden (salt okunur) bakarken her sayfanın en üstünde, yapışkan.
 * Turuncu-kahve "dikkat" tonu (waiting token'ları); yalnız renkle değil metinle de anlatır. Kalan süre
 * dakikada bir güncellenir; süre dolunca oturum yenilenir (sunucu 401 → "destek oturumu sona erdi" ekranı).
 */
export function SupportBanner({ support, tenantName, viewerName }: { support: SupportInfo; tenantName: string; viewerName: string }) {
  const t = useT();
  const { refresh } = useSession();
  const [left, setLeft] = useState(() => minutesLeft(support.expiresAt));
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    const tick = () => {
      const m = minutesLeft(support.expiresAt);
      setLeft(m);
      if (m <= 0) void refreshRef.current();
    };
    tick();
    const id = window.setInterval(tick, 15_000);
    return () => window.clearInterval(id);
  }, [support.expiresAt]);

  // Telefonda üst çubuk şeridin altına yapışsın diye şerit yüksekliği kabuğa CSS değişkeni olarak yazılır.
  useEffect(() => {
    const el = ref.current;
    const shell = el?.closest<HTMLElement>(".shell");
    if (!el || !shell) return;
    const set = () => shell.style.setProperty("--support-h", `${el.offsetHeight}px`);
    set();
    const ro = typeof ResizeObserver === "function" ? new ResizeObserver(set) : null;
    ro?.observe(el);
    document.documentElement.dataset.support = "1";
    return () => {
      ro?.disconnect();
      delete document.documentElement.dataset.support;
    };
  }, []);

  return (
    <div ref={ref} className="support-banner" role="region" aria-label={t("platform.banner.label")}>
      <Icon name="eye" size={20} className="support-banner__icon" />
      {/* Telefonda kısa satır görünür ("Destek · Ad · Salt okunur · 24 dk kaldı"); tam cümle ekran okuyucuda kalır. */}
      <p className="support-banner__text">
        <span className="support-banner__long">
          <strong>{t("platform.banner.title")}</strong>
          <span aria-hidden>{" · "}</span>
          <span>{tenantName}</span>
          <span aria-hidden>{" · "}</span>
          <span>{t("platform.banner.viewingAs", { name: viewerName })}</span>
        </span>
        <span className="support-banner__short" aria-hidden>
          <strong>{t("platform.banner.short", { name: viewerName })}</strong>
        </span>
        <span aria-hidden>{" · "}</span>
        <span className="support-banner__tag">{t("platform.banner.readOnly")}</span>
        <span aria-hidden>{" · "}</span>
        <span className="support-banner__left" aria-live={left <= 5 ? "polite" : "off"} data-testid="support-left">
          {t("platform.common.minutesLeft", { count: left })}
        </span>
      </p>
      <button
        type="button"
        className="btn btn--secondary btn--sm support-banner__end"
        disabled={busy}
        aria-busy={busy || undefined}
        onClick={() => {
          setBusy(true);
          void endSupportAndReturn();
        }}
      >
        <Icon name="sign-out" size={16} />
        <span>{t("platform.banner.end")}</span>
      </button>
    </div>
  );
}
